import {ChangeDetectionStrategy, Component, Inject, Optional} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatNativeDateModule} from '@angular/material/core';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {firstValueFrom} from 'rxjs';
import {VAT_TRANSMITTED_COPY} from '../../vat-copy';

export interface VatTransmittedDialogData {
    periodLabelText: string;
}

export interface VatTransmittedDialogResult {
    am: Date;
    transferticket: string | null;
}

/** Kompatibilitäts-Alias für Plan 3 (`vat-return-page.component.ts`). */
export type VatTransmittedResult = VatTransmittedDialogResult;

/**
 * „Als übermittelt markieren“ (Spec 8): Datum (Default heute, nie Zukunft) und optionales Transferticket.
 * Schreibt nichts – der Aufrufer (Plan-3-Seite bzw. Plan-4-Fassade) ruft den Workflow `markUebermittelt` (A1).
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
@Component({
    selector: 'vat-transmitted-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    // MatNativeDateModule liefert den DateAdapter für den Datepicker lokal (kein globaler Adapter vorausgesetzt).
    imports: [FormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatNativeDateModule],
    template: `
        <h2 mat-dialog-title class="text-lg font-semibold">{{ copy.title }}{{ data.periodLabelText ? ' – ' + data.periodLabelText : '' }}</h2>
        <mat-dialog-content class="flex flex-col gap-4">
            <p class="text-secondary text-md">{{ copy.text }}</p>
            <mat-form-field class="w-full" subscriptSizing="dynamic">
                <mat-label>{{ copy.dateLabel }}</mat-label>
                <input matInput [matDatepicker]="picker" [(ngModel)]="am" [max]="today" name="am" cdkFocusInitial>
                <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                <mat-datepicker #picker></mat-datepicker>
            </mat-form-field>
            <mat-form-field class="w-full" subscriptSizing="dynamic">
                <mat-label>{{ copy.ticketLabel }}</mat-label>
                <input matInput [(ngModel)]="transferticket" name="transferticket" maxlength="64">
            </mat-form-field>
        </mat-dialog-content>
        <mat-dialog-actions align="end">
            <button mat-stroked-button type="button" (click)="dialogRef.close(undefined)">{{ copy.cancel }}</button>
            <button mat-flat-button color="primary" type="button" [disabled]="!canConfirm()" (click)="confirm()">{{ copy.confirm }}</button>
        </mat-dialog-actions>
    `
})
export class VatTransmittedDialogComponent {
    readonly copy = VAT_TRANSMITTED_COPY;
    readonly today = new Date();
    readonly data: VatTransmittedDialogData;
    am: Date = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate());
    transferticket = '';

    constructor(
        public dialogRef: MatDialogRef<VatTransmittedDialogComponent, VatTransmittedDialogResult | undefined>,
        // Optional: Plan 3 öffnet den Dialog ohne `data`; dann entfällt der Zeitraum im Titel.
        @Optional() @Inject(MAT_DIALOG_DATA) data: VatTransmittedDialogData | null,
    ) {
        this.data = data ?? {periodLabelText: ''};
    }

    canConfirm(): boolean {
        return this.am instanceof Date && !isNaN(this.am.getTime()) && this.am.getTime() <= this.today.getTime();
    }

    confirm(): void {
        if (!this.canConfirm()) {
            return;
        }
        const ticket = (this.transferticket ?? '').trim();
        this.dialogRef.close({am: this.am, transferticket: ticket.length ? ticket : null});
    }
}

/** Kompatibilitäts-Helfer für Plan 3: öffnet den Dialog und liefert das Ergebnis oder `null` bei Abbruch. */
export const openTransmittedDialog = (matDialog: MatDialog, periodLabelText: string = ''): Promise<VatTransmittedResult | null> =>
    firstValueFrom(matDialog.open<VatTransmittedDialogComponent, VatTransmittedDialogData, VatTransmittedDialogResult | undefined>(VatTransmittedDialogComponent, {
        width: '480px', maxWidth: '95vw', autoFocus: true, data: {periodLabelText},
    }).afterClosed()).then(result => result ?? null);
